import { z } from "zod";
import { optionalUuid } from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

export const TransactionSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  check_number: z.number().int().nullable().describe("Check Number"),
  expense_type: z.string().nullable().describe("Expense Type"),
  transaction_date: z.string().nullable().describe("Transaction Date"),
  amount: z.number().nullable().describe("Amount"),
  transaction_type: z.string().nullable().describe("Transaction Type"),
  payment_type: z.string().nullable().describe("Payment Type"),
  sales_tax: z.number().nullable().describe("Sales Tax"),
  vendor_contact_role_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Vendor Contact Role"),
  vendor_invoice: z.string().nullable().describe("Vendor Invoice"),
  document_url: z.string().nullable().describe("Document URL"),
  notes: z.string().nullable().describe("Notes"),
  review_status: z.string().nullable().describe("Review Status"),
  category: z.string().nullable().describe("Category"),
  is_approved: z.boolean().describe("Approved"),
  approval_time: z.string().nullable().describe("Approval Time"),
  job_id: z.string().uuid().nullable().describe("Job"),
  employee_role_id: z.string().uuid().nullable().describe("Employee"),
  crew_id: z.string().uuid().nullable().describe("Crew"),
  warranty_id: z.string().uuid().nullable().describe("Warranty"),
  invoice_id: z.string().uuid().nullable().describe("Invoice"),
  pay_period_id: z.string().uuid().nullable().describe("Pay Period"),
  territory_id: z.string().uuid().nullable().describe("Territory"),
  // ADR-0048 (ADV-1177): recurring-billing charges generated from an agreement
  // cadence link back to finance.service_agreements.
  service_agreement_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Service Agreement"),
  // ADV-892 added finance.transactions.salesperson_user_id; list/detail read
  // paths select("*") so the read schema must accept it or .parse() will strip
  // nothing but type-narrow incorrectly. Insert/Update intentionally omit it
  // (set server-side, mirroring invoices' commission-attribution copy).
  salesperson_user_id: z.string().uuid().nullable().describe("Salesperson"),
  credit_card_fee: z.number().nullable().describe("Credit Card Fee"),
  is_check_cut: z.boolean().nullable().describe("Check Cut"),
  is_deposit: z.boolean().nullable().describe("Deposit"),
  details: z.record(z.unknown()).nullable().describe("Details"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type Transaction = z.infer<typeof TransactionSchema>;

export const TransactionInsertSchema = z.object({
  check_number: z.number().int().optional().describe("Check Number"),
  expense_type: z.string().optional().describe("Expense Type"),
  transaction_date: z.string().optional().describe("Transaction Date"),
  amount: z.number().optional().describe("Amount"),
  transaction_type: z.string().optional().describe("Transaction Type"),
  payment_type: z.string().optional().describe("Payment Type"),
  sales_tax: z.number().optional().describe("Sales Tax"),
  vendor_contact_role_id: optionalUuid().describe("Vendor Contact Role"),
  vendor_invoice: z.string().optional().describe("Vendor Invoice"),
  document_url: z.string().optional().describe("Document URL"),
  notes: z.string().optional().describe("Notes"),
  review_status: z.string().optional().describe("Review Status"),
  category: z.string().optional().describe("Category"),
  is_approved: z.boolean().default(false).describe("Approved"),
  approval_time: z.string().optional().describe("Approval Time"),
  job_id: optionalUuid().describe("Job"),
  employee_role_id: optionalUuid().describe("Employee"),
  crew_id: optionalUuid().describe("Crew"),
  warranty_id: optionalUuid().describe("Warranty"),
  invoice_id: optionalUuid().describe("Invoice"),
  pay_period_id: optionalUuid().describe("Pay Period"),
  territory_id: optionalUuid().describe("Territory"),
  service_agreement_id: optionalUuid().describe("Service Agreement"),
  credit_card_fee: z.number().optional().describe("Credit Card Fee"),
  is_check_cut: z.boolean().optional().describe("Check Cut"),
  is_deposit: z.boolean().optional().describe("Deposit"),
  details: z.any().optional().describe("Details"),
  custom_fields: z.any().optional().describe("Custom Fields"),
});

export const TransactionUpdateSchema = TransactionInsertSchema.partial();

// Query/filter schema for the list endpoint. Query schemas are excluded from the
// `.describe()` label convention (they are request params, not entity fields).
// transaction_type / payment_type are free-text imported values (no DB enum), so
// they filter by exact string match rather than an enum.
export const TransactionQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  transaction_type: z.string().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  payment_type: z.string().optional(),
  // schema-check-ignore: query-filter — string flag avoids z.coerce.boolean's "false"->true footgun
  is_approved: z.enum(["true", "false"]).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  invoice_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  opportunity_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  job_id: z.string().uuid().optional(),
  date_from: z
    .string()
    .optional()
    .describe("Filter transaction_date >= this date (YYYY-MM-DD)"),
  date_to: z
    .string()
    .optional()
    .describe("Filter transaction_date <= this date (YYYY-MM-DD)"),
  search: z.string().optional().describe("Search vendor_invoice"),
});
